import React, { useEffect, useState } from 'react';
import { reports } from '../lib/api';
import { inr, rs } from '../lib/format';
import { downloadPdfReport } from '../lib/pdfReport';
import { Download, FileBarChart } from 'lucide-react';
import { format } from 'date-fns';

export const MonthlyReportModal = ({ onClose }) => {
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [packLoading, setPackLoading] = useState(false);

  const [y, m] = month.split('-').map(Number);
  const monthLabel = format(new Date(y, m - 1, 1), 'MMMM yyyy');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await reports.pnl(y, m);
        setPnl(r.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [y, m]);

  const downloadPnlPdf = () => {
    if (!pnl) return;
    downloadPdfReport({
      title: `Profit & Loss — ${monthLabel}`,
      subtitle: 'Sales − Cost of Goods = Gross Profit − Approved Operating Expenses = Net',
      summary: [
        { label: 'Sales', value: rs(pnl.sales) },
        { label: 'COGS', value: rs(pnl.cogs) },
        { label: 'Gross Profit', value: `${rs(pnl.gross_profit)} (${pnl.gross_margin_pct}%)` },
        { label: 'Net', value: rs(pnl.net) },
        { label: 'Kg Dispatched', value: `${pnl.kg_dispatched} kg` },
        { label: 'Collected', value: rs(pnl.amount_collected) },
      ],
      tables: [
        {
          title: 'Operating Expenses by Category (Approved)',
          head: ['Category', 'Amount', 'In P&L?'],
          rows: pnl.expenses_by_category.map((e) => [
            e.category, rs(e.amount), e.excluded_from_pnl ? 'Excluded (bean purchase = COGS)' : 'Yes',
          ]),
        },
      ],
      filename: `pnl_${month}.pdf`,
    });
  };

  const downloadPackPdf = async () => {
    setPackLoading(true);
    try {
      const r = await reports.monthlyPack(y, m);
      const pack = r.data;
      const p = pack.pnl;
      downloadPdfReport({
        title: `Monthly Report Pack — ${monthLabel}`,
        subtitle: 'Full operations & financial summary',
        summary: [
          { label: 'Sales', value: rs(p.sales) },
          { label: 'Gross Profit', value: rs(p.gross_profit) },
          { label: 'Op. Expenses', value: rs(p.operating_expenses) },
          { label: 'Net', value: rs(p.net) },
          { label: 'Kg Dispatched', value: `${p.kg_dispatched} kg` },
          { label: 'Wastage Cost', value: rs(pack.total_wastage_cost) },
        ],
        tables: [
          {
            title: 'Green Stock Movement',
            head: ['Category', 'Opening (kg)', '+ Intakes', '− Roasted', 'Closing (kg)'],
            rows: pack.green_movement.map((g) => [
              g.category_name, g.opening_kg.toFixed(2), `+${g.intake_kg.toFixed(2)}`,
              `-${g.roasted_kg.toFixed(2)}`, g.closing_kg.toFixed(2),
            ]),
          },
          {
            title: 'Roasted Stock Movement (by Level)',
            head: ['Roast Level', 'Opening (kg)', '+ Roasted', '− Dispatched', 'Closing (kg)'],
            rows: pack.roasted_movement.map((r2) => [
              r2.roast_level, r2.opening_kg.toFixed(2), `+${r2.added_kg.toFixed(2)}`,
              `-${r2.dispatched_kg.toFixed(2)}`, r2.closing_kg.toFixed(2),
            ]),
          },
          {
            title: 'Roasts',
            head: ['Date', 'Batch', 'Category', 'Level', 'Green (kg)', 'Roasted (kg)', 'Yield %', 'Wastage Cost'],
            rows: pack.roasts.map((r2) => [
              format(new Date(r2.date), 'MMM dd'), r2.batch_no, r2.category_name, r2.roast_level,
              r2.green_kg.toFixed(2), r2.roasted_kg.toFixed(2), `${r2.yield_pct}%`, rs(r2.wastage_cost),
            ]),
          },
          {
            title: 'Dispatches',
            head: ['Date', 'Invoice', 'Client', 'Items', 'Kg', 'Sale', 'Cost', 'Payment'],
            rows: pack.dispatches.map((d) => [
              format(new Date(d.date), 'MMM dd'), d.invoice_no, d.client_name, d.items,
              d.total_kg.toFixed(2), rs(d.sale), rs(d.cost), d.payment_status,
            ]),
          },
          {
            title: 'Approved Expenses by Category',
            head: ['Category', 'Amount', 'In P&L?'],
            rows: p.expenses_by_category.map((e) => [
              e.category, rs(e.amount), e.excluded_from_pnl ? 'Excluded' : 'Yes',
            ]),
          },
        ],
        filename: `monthly-report-pack_${month}.pdf`,
      });
    } catch (e) {
      alert('Failed to build report pack');
    } finally {
      setPackLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] flex flex-col">
        <div className="px-6 py-4 border-b flex justify-between items-center shrink-0">
          <div className="flex items-center gap-2">
            <FileBarChart className="w-5 h-5 text-amber-600" />
            <h2 className="text-xl font-bold text-gray-800">Monthly Report</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl" data-testid="monthly-report-close">×</button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <input type="month" value={month} max={format(new Date(), 'yyyy-MM')}
              onChange={(e) => e.target.value && setMonth(e.target.value)}
              data-testid="report-month-input"
              className="px-3 py-2 border rounded-lg" />
          </div>

          {loading || !pnl ? (
            <p className="text-center text-gray-400 py-8 text-sm">Loading P&L…</p>
          ) : (
            <div className="border rounded-xl overflow-hidden" data-testid="pnl-summary">
              <div className="divide-y">
                <Row label="Sales" value={inr(pnl.sales)} />
                <Row label="− Cost of Goods (COGS)" value={inr(pnl.cogs)} muted />
                <Row label="Gross Profit" value={`${inr(pnl.gross_profit)} (${pnl.gross_margin_pct}%)`} bold color="text-emerald-700" />
                <Row label="− Operating Expenses (approved)" value={inr(pnl.operating_expenses)} muted />
                {pnl.excluded_expenses > 0 && (
                  <Row label="Bean purchases (excluded — already in COGS)" value={inr(pnl.excluded_expenses)} muted small />
                )}
                <Row label="Net" value={inr(pnl.net)} bold color={pnl.net >= 0 ? 'text-emerald-700' : 'text-red-600'} big />
                <Row label="Collected from clients" value={inr(pnl.amount_collected)} muted small />
                <Row label={`Dispatches: ${pnl.dispatch_count} · ${pnl.kg_dispatched} kg`} value="" muted small />
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button onClick={downloadPnlPdf} disabled={!pnl}
              data-testid="download-pnl-pdf"
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-2.5 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition disabled:opacity-50">
              <Download className="w-4 h-4" /> P&L PDF
            </button>
            <button onClick={downloadPackPdf} disabled={packLoading}
              data-testid="download-report-pack-pdf"
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2.5 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition disabled:opacity-50">
              <Download className="w-4 h-4" /> {packLoading ? 'Building…' : 'Full Report Pack'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold, muted, color = '', big, small }) => (
  <div className={`flex justify-between items-center px-4 ${big ? 'py-3 bg-gray-50' : small ? 'py-1.5' : 'py-2.5'}`}>
    <p className={`${small ? 'text-xs' : 'text-sm'} ${muted ? 'text-gray-500' : 'text-gray-800'} ${bold ? 'font-bold' : ''}`}>{label}</p>
    <p className={`${big ? 'text-lg' : small ? 'text-xs' : 'text-sm'} font-${bold ? 'bold' : 'semibold'} ${color || 'text-gray-800'}`}>{value}</p>
  </div>
);
